import { useEffect, useState } from "react";
import { useParams, Link, useNavigate, useSearchParams } from "react-router-dom";
import { api, API } from "../lib/api";
import { Crown, Star, ShieldCheck, BookOpen, Calendar, ArrowRight, Sparkles, Quote } from "lucide-react";
import BrandMark from "../components/BrandMark";
import { SiteFooter } from "../components/Layout";
import FounderShareCard from "../components/FounderShareCard";
import FounderNFTBadge from "../components/FounderNFTBadge";
import { hideOnError } from "../lib/imgFallback";

/**
 * ElderProfile — fully PUBLIC SEO-indexable profile for a single Founder Elder.
 *
 * - No Layout / no auth required (so search-engine crawlers and unsigned
 *   visitors can fully see it).
 * - Sets <title>, <meta description>, OpenGraph + Twitter cards so links
 *   shared on social shower a rich preview card.
 * - Conversion CTAs: "Sign in to book" + "Become a Practitioner".
 */
export default function ElderProfile() {
  const { uid } = useParams();
  const [searchParams] = useSearchParams();
  const fromSource = searchParams.get("from");
  const nav = useNavigate();
  const [elder, setElder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/elders/${uid}`, {
          params: fromSource ? { from: fromSource } : {},
        });
        setElder(data);
      } catch (e) {
        setNotFound(true);
      } finally {
        setLoading(false);
      }
    })();
  }, [uid, fromSource]);

  // SEO meta-tag side-effects.
  useEffect(() => {
    if (!elder) return;
    const displayName = elder.businessName || elder.name || "Cauldron Elder";
    const idxLabel = elder.elderIndex
      ? `Founder #${String(elder.elderIndex).padStart(4, "0")} of 500`
      : "Cauldron Founder Elder";
    const desc =
      (elder.bio && elder.bio.slice(0, 155).trim()) ||
      [
        `${displayName} — ${idxLabel}.`,
        elder.tradition ? `${elder.tradition} practitioner.` : null,
        elder.category ? `Specializing in ${elder.category}.` : null,
        "Verified Elder on Cauldron, the social marketplace for spiritual practitioners.",
      ].filter(Boolean).join(" ");
    document.title = `${displayName} · ${idxLabel} · Cauldron`;
    setMeta("description", desc);
    setMeta("og:title", `${displayName} · ${idxLabel}`, "property");
    setMeta("og:description", desc, "property");
    setMeta("og:type", "profile", "property");
    setMeta("og:url", window.location.href, "property");
    if (elder.avatarUrl) {
      const img = elder.avatarUrl.startsWith("http") ? elder.avatarUrl : `${API}${elder.avatarUrl}`;
      setMeta("og:image", img, "property");
      setMeta("twitter:image", img);
    }
    setMeta("twitter:card", "summary_large_image");
    setMeta("twitter:title", `${displayName} · Cauldron Elder`);
    setMeta("twitter:description", desc);
    return () => { document.title = "Cauldron"; };
  }, [elder]);

  // schema.org JSON-LD structured data — unlocks Google rich-card results
  // (star ratings, service catalog, business knowledge panel). Uses an
  // `@graph` array so we can emit BOTH a ProfessionalService node (for
  // local-business rich cards) AND a Person node (for knowledge-panel
  // attribution to the human practitioner). Cleans up on unmount.
  useEffect(() => {
    if (!elder) return;
    const displayName = elder.businessName || elder.name || "Cauldron Elder";
    const personName = elder.name || displayName;
    const url = window.location.origin + `/elders/${elder.userId}`;
    const img = elder.avatarUrl
      ? (elder.avatarUrl.startsWith("http") ? elder.avatarUrl : `${API}${elder.avatarUrl}`)
      : undefined;
    const fullDesc =
      elder.lineageStatement ||
      elder.bio ||
      `${displayName} — Verified Founder Elder on Cauldron, the social marketplace for spiritual practitioners.`;

    // knowsAbout: the things this practitioner specializes in.
    const knowsAbout = [elder.tradition, elder.category].filter(Boolean);

    // Offers: each active service becomes a schema.org Offer + nested Service.
    const offers = (elder.services || []).map((s) => ({
      "@type": "Offer",
      name: s.title,
      description: s.description || undefined,
      price: String(s.price ?? 0),
      priceCurrency: "USD",
      availability: "https://schema.org/InStock",
      itemOffered: {
        "@type": "Service",
        name: s.title,
        description: s.description || undefined,
        provider: { "@type": "Person", name: personName },
      },
    }));

    const aggregateRating =
      elder.avgRating != null && elder.reviewCount > 0
        ? {
            "@type": "AggregateRating",
            ratingValue: String(elder.avgRating),
            reviewCount: String(elder.reviewCount),
            bestRating: "5",
            worstRating: "1",
          }
        : undefined;

    // Outer ProfessionalService node (drives local-business rich card)
    const professionalService = {
      "@type": "ProfessionalService",
      "@id": url,
      name: displayName,
      url,
      image: img,
      description: fullDesc.slice(0, 500),
      areaServed: { "@type": "Place", name: "Worldwide" },
      ...(knowsAbout.length ? { knowsAbout } : {}),
      ...(offers.length ? { makesOffer: offers } : {}),
      ...(aggregateRating ? { aggregateRating } : {}),
      ...(elder.yearsInitiated
        ? { foundingDate: new Date().getFullYear() - elder.yearsInitiated + "" }
        : {}),
      founder: {
        "@type": "Person",
        name: personName,
        ...(img ? { image: img } : {}),
      },
    };

    // Standalone Person node — for knowledge panel attribution
    const person = {
      "@type": "Person",
      "@id": `${url}#practitioner`,
      name: personName,
      jobTitle: "Spiritual Practitioner",
      url,
      ...(img ? { image: img } : {}),
      ...(knowsAbout.length ? { knowsAbout } : {}),
      description: fullDesc.slice(0, 500),
      ...(elder.temple ? { affiliation: { "@type": "Organization", name: elder.temple } } : {}),
      memberOf: { "@type": "Organization", name: "Cauldron Founder Elders" },
    };

    const graph = {
      "@context": "https://schema.org",
      "@graph": [professionalService, person],
    };

    // JSON.stringify naturally drops undefined values, so no replacer needed.
    const json = JSON.stringify(graph);

    let script = document.getElementById("elder-jsonld");
    if (!script) {
      script = document.createElement("script");
      script.type = "application/ld+json";
      script.id = "elder-jsonld";
      document.head.appendChild(script);
    }
    script.textContent = json;

    return () => {
      const s = document.getElementById("elder-jsonld");
      if (s) s.remove();
    };
  }, [elder]);

  if (loading) {
    return (
      <div className="dark-theme min-h-screen flex items-center justify-center font-serif text-3xl text-foreground/70">
        Gathering the lineage…
      </div>
    );
  }

  if (notFound || !elder) {
    return (
      <div className="dark-theme min-h-screen flex flex-col items-center justify-center p-8 text-center" data-testid="elder-not-found">
        <BrandMark size={64} />
        <h1 className="font-serif text-5xl mt-6">This founder isn't on the wall</h1>
        <p className="text-foreground/65 mt-3 max-w-md">
          They may have stepped back from the coven. Browse the full roster of founders below.
        </p>
        <Link to="/elders" className="mt-6 bg-primary text-primary-foreground px-6 py-3 rounded-sm uppercase tracking-[0.2em] text-xs hover:bg-primary/90" data-testid="elder-back-to-wall">
          See all Founders
        </Link>
      </div>
    );
  }

  const displayName = elder.businessName || elder.name;
  const initials = (elder.name || "?").split(" ").map((s) => s[0]).join("").slice(0, 2).toUpperCase();
  const avatarSrc = elder.avatarUrl?.startsWith("http") ? elder.avatarUrl : (elder.avatarUrl ? `${API}${elder.avatarUrl}` : null);
  const bannerSrc = elder.bannerUrl?.startsWith("http") ? elder.bannerUrl : (elder.bannerUrl ? `${API}${elder.bannerUrl}` : null);

  return (
    <div className="dark-theme min-h-screen relative" data-testid="elder-profile-page">
      {/* — Top Bar — */}
      <nav className="relative z-20 max-w-[1600px] mx-auto px-6 py-5 flex justify-between items-center">
        <Link to="/" className="flex items-center gap-3" data-testid="elder-profile-brand">
          <BrandMark size={36} />
          <span className="font-serif text-2xl">cauldron</span>
        </Link>
        <div className="flex items-center gap-3">
          <Link to="/elders" className="text-xs uppercase tracking-[0.2em] text-foreground/70 hover:text-secondary" data-testid="elder-profile-allelders-link">
            All Founders
          </Link>
          <Link to="/auth?mode=login" className="text-xs uppercase tracking-[0.2em] text-foreground/85 hover:text-secondary px-3 py-2" data-testid="elder-profile-login-link">
            Sign In
          </Link>
          <Link to="/auth?mode=register" className="text-xs uppercase tracking-[0.2em] bg-primary text-primary-foreground px-4 py-2 rounded-sm hover:bg-primary/90" data-testid="elder-profile-register-link">
            Begin
          </Link>
        </div>
      </nav>

      {/* — Banner — */}
      <div className="relative h-[340px] overflow-hidden">
        {bannerSrc ? (
          <img src={bannerSrc} alt="" className="w-full h-full object-cover opacity-60" onError={hideOnError} />
        ) : (
          <div className="w-full h-full"
               style={{ background: "radial-gradient(ellipse at 30% 50%, rgba(217,92,20,0.25), transparent 60%), radial-gradient(ellipse at 70% 50%, rgba(197,160,89,0.18), transparent 65%), #1A1530" }} />
        )}
        <div className="absolute inset-0 bg-gradient-to-b from-background/40 via-background/60 to-background" />
      </div>

      {/* — Identity Card (sits over banner) — */}
      <div className="max-w-5xl mx-auto px-6 -mt-32 relative z-10">
        <div className="parchment p-8 sm:p-10">
          <div className="flex flex-col sm:flex-row gap-8 items-center sm:items-end">
            {/* Avatar with gold ring */}
            <div className="relative shrink-0">
              <div
                className="absolute pointer-events-none rounded-full"
                style={{
                  inset: "-9px",
                  background: "conic-gradient(from 140deg, #C5A059 0deg, #D95C14 90deg, #F4D58E 180deg, #C5A059 270deg, #D95C14 360deg)",
                  padding: "4px",
                  WebkitMask: "linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)",
                  WebkitMaskComposite: "xor",
                  maskComposite: "exclude",
                  boxShadow: "0 0 28px rgba(217,92,20,0.55)",
                }}
              />
              <div className="relative w-36 h-36 rounded-full overflow-hidden bg-secondary/15 border-2 border-card flex items-center justify-center">
                {avatarSrc ? (
                  <img src={avatarSrc} alt={displayName} className="w-full h-full object-cover" onError={hideOnError} data-testid="elder-profile-avatar" />
                ) : (
                  <span className="font-serif text-5xl text-secondary">{initials}</span>
                )}
              </div>
              <span
                className="absolute flex items-center justify-center rounded-full"
                style={{
                  top: "-9px",
                  right: "-9px",
                  width: 40,
                  height: 40,
                  background: "linear-gradient(135deg,#D95C14 0%,#C5A059 60%,#F4D58E 100%)",
                  color: "#15102A",
                  boxShadow: "0 4px 14px rgba(0,0,0,0.45), 0 0 0 3px #15102A",
                }}
              >
                <Crown size={20} strokeWidth={2.25} fill="currentColor" />
              </span>
            </div>

            <div className="flex-1 text-center sm:text-left min-w-0">
              <div className="label-mystic text-secondary flex items-center justify-center sm:justify-start gap-2">
                <Crown size={11} /> Verified Founder Elder
                {elder.elderIndex && (
                  <span className="text-foreground/55">· #{String(elder.elderIndex).padStart(4, "0")} of 500</span>
                )}
              </div>
              <h1 className="font-serif text-4xl sm:text-5xl mt-2 leading-tight" data-testid="elder-profile-name">
                {displayName}
              </h1>
              {elder.tradition && (
                <div className="text-sm text-secondary mt-2 uppercase tracking-[0.18em]" data-testid="elder-profile-tradition">
                  {elder.tradition} {elder.yearsInitiated ? `· ${elder.yearsInitiated}+ years` : ""}
                </div>
              )}
              {elder.category && (
                <div className="text-sm text-foreground/65 mt-1">Specialty · {elder.category}</div>
              )}

              <div className="flex gap-3 mt-5 justify-center sm:justify-start flex-wrap">
                <button onClick={() => nav(`/auth?mode=login&next=/profile/${elder.userId}`)}
                        className="bg-primary text-primary-foreground px-5 py-3 rounded-sm uppercase tracking-[0.18em] text-xs hover:bg-primary/90 inline-flex items-center gap-2"
                        data-testid="elder-profile-book-cta">
                  <Calendar size={13} /> Sign in to book <ArrowRight size={12} />
                </button>
                <Link to="/elders"
                      className="border border-secondary text-secondary px-5 py-3 rounded-sm uppercase tracking-[0.18em] text-xs hover:bg-secondary/10"
                      data-testid="elder-profile-see-all">
                  See all 500 Founders
                </Link>
                <FounderShareCard elder={elder} />
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-6 mt-10 grid grid-cols-1 md:grid-cols-3 gap-6">
        {/* — Bio — */}
        <div className="md:col-span-2 space-y-6">
          <FounderNFTBadge userId={elder.userId} />
          {elder.bio && (
            <section className="parchment p-6" data-testid="elder-profile-bio-section">
              <div className="label-mystic mb-3 inline-flex items-center gap-2 text-secondary">
                <BookOpen size={11} /> About
              </div>
              <p className="text-foreground/85 leading-relaxed whitespace-pre-line text-[15px]">{elder.bio}</p>
            </section>
          )}

          {/* — Lineage — */}
          {(elder.tradition || elder.teacher || elder.temple || elder.lineageStatement || (elder.certifications || []).length > 0) && (
            <section className="parchment p-6 space-y-4" data-testid="elder-profile-lineage-section">
              <div className="label-mystic inline-flex items-center gap-2 text-secondary">
                <ShieldCheck size={11} /> Lineage & Practice
              </div>

              {elder.lineageStatement && (
                <div className="relative pl-7">
                  <Quote size={18} className="absolute top-0 left-0 text-secondary/60" strokeWidth={1.5} />
                  <p className="font-serif italic text-lg text-foreground/90 leading-relaxed">{elder.lineageStatement}</p>
                </div>
              )}

              <div className="grid sm:grid-cols-2 gap-x-6 gap-y-3 text-sm">
                {elder.tradition && <LineageRow label="Tradition" value={elder.tradition} />}
                {elder.teacher && <LineageRow label="Teacher / Initiator" value={elder.teacher} />}
                {elder.temple && <LineageRow label="Temple / House" value={elder.temple} />}
                {elder.yearsInitiated != null && <LineageRow label="Years Initiated" value={`${elder.yearsInitiated}+`} />}
              </div>

              {(elder.certifications || []).length > 0 && (
                <div>
                  <div className="label-mystic text-foreground/55 mb-2">Certifications</div>
                  <div className="flex flex-wrap gap-2">
                    {elder.certifications.map((c, i) => (
                      <span key={i} className="text-[11px] uppercase tracking-[0.18em] px-2 py-1 border border-secondary/30 text-secondary/85 rounded-sm">{c}</span>
                    ))}
                  </div>
                </div>
              )}

              {elder.templeVerified && (
                <div className="text-xs text-secondary inline-flex items-center gap-1.5">
                  <ShieldCheck size={12} /> Temple verified · business doc on file
                </div>
              )}
            </section>
          )}

          {/* — Services — */}
          {elder.services?.length > 0 && (
            <section className="parchment p-6" data-testid="elder-profile-services-section">
              <div className="flex justify-between items-end mb-4">
                <div>
                  <div className="label-mystic text-secondary">Services Offered</div>
                  <h2 className="font-serif text-2xl mt-1">{elder.services.length} ritual{elder.services.length === 1 ? "" : "s"} available</h2>
                </div>
                <div className="text-xs text-foreground/55 hidden sm:block">Sign in to book</div>
              </div>
              <div className="space-y-3">
                {elder.services.slice(0, 6).map((s) => (
                  <div key={s.id} className="border-l-2 border-secondary/40 pl-4 py-1" data-testid={`elder-service-${s.id}`}>
                    <div className="flex justify-between items-baseline gap-3">
                      <div className="font-serif text-lg">{s.title}</div>
                      <div className="font-serif text-xl text-secondary shrink-0">${(s.price || 0).toFixed(0)}</div>
                    </div>
                    {s.durationMinutes && (
                      <div className="text-[11px] uppercase tracking-[0.18em] text-foreground/55">{s.durationMinutes} min</div>
                    )}
                    {s.description && (
                      <p className="text-foreground/70 text-sm mt-1 line-clamp-2">{s.description}</p>
                    )}
                  </div>
                ))}
              </div>
            </section>
          )}
        </div>

        {/* — Right rail — */}
        <aside className="space-y-4">
          <div className="parchment p-5 text-center">
            <div className="label-mystic text-secondary mb-2">Trust Score</div>
            {elder.avgRating != null ? (
              <>
                <div className="font-serif text-5xl text-secondary flex items-center justify-center gap-1" data-testid="elder-profile-rating">
                  {elder.avgRating}
                  <Star size={18} strokeWidth={1.5} fill="currentColor" className="text-secondary" />
                </div>
                <div className="text-xs text-foreground/55 mt-1">
                  {elder.reviewCount} verified review{elder.reviewCount === 1 ? "" : "s"}
                </div>
              </>
            ) : (
              <div className="font-serif text-xl text-foreground/60 mt-2">No reviews yet</div>
            )}
          </div>

          <div className="parchment p-5">
            <div className="label-mystic text-secondary mb-2">Status</div>
            <div className="space-y-2 text-sm">
              <Stat label="Founder Rank" value={elder.elderIndex ? `#${String(elder.elderIndex).padStart(4, "0")}` : "—"} />
              <Stat label="Services" value={elder.serviceCount} />
              <Stat label="Joined" value={elder.createdAt ? new Date(elder.createdAt).toLocaleDateString("en", { year: "numeric", month: "short" }) : "—"} />
            </div>
          </div>

          <Link to="/auth?mode=register&role=CLIENT"
                className="block parchment p-5 hover:border-secondary/60 transition-colors"
                data-testid="elder-profile-seek-cta">
            <Sparkles size={18} className="text-secondary mb-2" strokeWidth={1.5} />
            <div className="font-serif text-xl leading-snug">Begin a session with {displayName.split(" ")[0]}</div>
            <div className="text-xs text-foreground/65 mt-2 leading-relaxed">
              Create a free seeker account to book a 1-on-1 ritual.
            </div>
            <div className="text-xs uppercase tracking-[0.2em] text-secondary mt-3 inline-flex items-center gap-1">
              Seek guidance <ArrowRight size={11} />
            </div>
          </Link>
        </aside>
      </div>

      <div className="max-w-5xl mx-auto px-6 mt-16">
        <div className="gold-divider" />
      </div>

      <SiteFooter withBottomNavOffset={false} />
    </div>
  );
}

function LineageRow({ label, value }) {
  return (
    <div>
      <div className="label-mystic text-foreground/50 mb-0.5">{label}</div>
      <div className="text-foreground/90">{value}</div>
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="flex justify-between items-baseline">
      <span className="label-mystic text-foreground/55">{label}</span>
      <span className="font-serif text-base text-foreground/90">{value}</span>
    </div>
  );
}

// — helpers —
function setMeta(name, content, attr = "name") {
  if (!content) return;
  let el = document.querySelector(`meta[${attr}="${name}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, name);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}
